import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import StatusBadge from "./StatusBadge";
import { toast } from "sonner";
import {
  Copy, Check, Link2, Link2Off, PlayCircle, Users, RefreshCw,
  Sparkles, AlertTriangle, ExternalLink, Database, Loader2, UserPlus,
  ChevronDown, ChevronRight, Trash2, Search,
} from "lucide-react";

const REDIRECT_URI = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google-ads/callback`;

function CopyBtn({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const click = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <button onClick={click} data-testid={testId} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-border rounded-sm hover:bg-secondary">
      {copied ? <Check className="w-3 h-3 text-[hsl(var(--success))]" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function Field({ label, children, help }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      {children}
      {help && <div className="text-[11px] text-muted-foreground mt-1">{help}</div>}
    </div>
  );
}

function AccountsTable({ profileId, clients, logs, refreshLogs }) {
  const [accounts, setAccounts] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [q, setQ] = useState("");
  const [batchResults, setBatchResults] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/google-ads/profiles/${profileId}/accounts`);
      setAccounts(data);
    } catch (e) { /* noop */ }
  };

  const triggerSyncNow = async () => {
    setSyncRunning(true);
    setSyncResult(null);
    try {
      const { data } = await api.post("/google-ads/sync-now");
      setSyncResult(data);
      if (data.skipped) {
        toast.warning(`Sync skipped — ${data.reason}`);
      } else {
        toast.success(`Sync complete · ${data.succeeded}/${data.accounts_processed} ok · campaigns +${data.campaigns_inserted}~${data.campaigns_updated} · LPs +${data.landing_pages_inserted ?? 0}~${data.landing_pages_updated ?? 0}`);
      }
      refreshLogs?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncRunning(false);
    }
  };

  useEffect(() => { load(); }, [profileId]);

  const discover = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.post(`/google-ads/profiles/${profileId}/discover`);
      toast.success(`Discovered ${data.count} accounts`);
      load(); refreshLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discover failed");
    } finally { setDiscovering(false); }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/google-ads/accounts/${id}`, body);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const testOne = async (acc) => {
    try {
      const { data } = await api.post(`/google-ads/accounts/${acc.id}/test`, { days: 7 });
      setBatchResults({ results: [data] });
      toast[data.ok ? "success" : "error"](data.ok
        ? `${acc.descriptive_name}: ${data.summary.campaigns} campaigns`
        : `${acc.descriptive_name}: ${data.error?.slice(0, 80)}`);
      load(); refreshLogs();
    } catch (e) { toast.error("Test failed"); }
  };

  const batchTest = async () => {
    const selected = accounts.filter(a => a.active && !a.is_manager);
    if (selected.length === 0) return toast.error("Toggle at least one non-MCC account to Active");
    setBatchRunning(true);
    setBatchResults(null);
    try {
      const { data } = await api.post(`/google-ads/batch-test`, {
        account_ids: selected.map(a => a.id), days: 7,
      });
      setBatchResults(data);
      toast.success(`Batch: ${data.succeeded} ok, ${data.failed} failed`);
      load(); refreshLogs();
    } catch (e) { toast.error("Batch failed"); }
    finally { setBatchRunning(false); }
  };

  const filtered = accounts.filter(a =>
    !q || a.descriptive_name.toLowerCase().includes(q.toLowerCase()) || a.customer_id.includes(q)
  );
  const activeCount = accounts.filter(a => a.active && !a.is_manager).length;

  return (
    <div className="space-y-4" data-testid={`ga-profile-${profileId}-accounts`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input placeholder="Search account…" value={q} onChange={(e) => setQ(e.target.value)}
              className="h-9 pl-8 pr-3 w-64 border border-border rounded-sm text-sm" />
          </div>
          <span className="text-xs text-muted-foreground">{accounts.length} accounts · {activeCount} active</span>
          <span data-testid="ga-token-status" className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30">
            <Check className="w-3 h-3" /> Basic Access
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={discover}
            disabled={discovering}
            data-testid={`ga-discover-${profileId}`}
            className="h-9 px-3 flex items-center gap-1.5 text-xs font-medium border border-border hover:bg-secondary rounded-sm disabled:opacity-50"
          >
            {discovering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Discover / Refresh Accounts
          </button>
          <button
            onClick={triggerSyncNow}
            disabled={syncRunning}
            data-testid={`ga-sync-now-${profileId}`}
            className="h-9 px-3 flex items-center gap-1.5 text-xs font-medium border border-border hover:bg-secondary rounded-sm disabled:opacity-50"
          >
            {syncRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />} Sync Now
          </button>
          <button
            onClick={batchTest}
            disabled={batchRunning || activeCount === 0}
            data-testid={`ga-batch-test-${profileId}`}
            className="h-9 px-3 flex items-center gap-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50"
          >
            {batchRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Batch Test Active ({activeCount})
          </button>
        </div>
      </div>

      {syncResult && !syncResult.skipped && (
        <div data-testid="ga-sync-summary" className="mb-3 p-3 bg-secondary/40 border border-border rounded-sm text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold">Last sync</span>
            <span className="text-muted-foreground">{new Date(syncResult.synced_at).toLocaleString()}</span>
          </div>
          <div className="mt-1.5 grid grid-cols-3 md:grid-cols-7 gap-x-3 gap-y-1 text-[11px]">
            <div><span className="text-muted-foreground">Processed</span> <b className="tabular">{syncResult.accounts_processed}</b></div>
            <div><span className="text-muted-foreground">OK</span> <b className="tabular text-[hsl(var(--success))]">{syncResult.succeeded}</b></div>
            <div><span className="text-muted-foreground">Failed</span> <b className="tabular text-[hsl(var(--critical))]">{syncResult.failed}</b></div>
            <div><span className="text-muted-foreground">Camps +/~</span> <b className="tabular">{syncResult.campaigns_inserted}/{syncResult.campaigns_updated}</b></div>
            <div><span className="text-muted-foreground">Accts +/~</span> <b className="tabular">{syncResult.accounts_inserted}/{syncResult.accounts_updated}</b></div>
            <div><span className="text-muted-foreground">Metrics +/~</span> <b className="tabular">{syncResult.metrics_inserted}/{syncResult.metrics_updated}</b></div>
            <div><span className="text-muted-foreground">LPs +/~</span> <b className="tabular">{syncResult.landing_pages_inserted ?? 0}/{syncResult.landing_pages_updated ?? 0}</b></div>
          </div>
        </div>
      )}

      {accounts.length === 0 ? (
        <div className="bg-secondary/40 border border-border rounded-sm p-8 text-center text-sm text-muted-foreground">
          No accounts discovered yet. Click <b>Discover / Refresh Accounts</b> to fetch accessible Google Ads accounts (and any MCC children).
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Customer ID</th>
                <th>Account Name</th>
                <th>Type</th>
                <th>Parent MCC</th>
                <th>Currency</th>
                <th>Active for sync</th>
                <th>Map to Client</th>
                <th>Last test</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const last = a.last_test_result;
                return (
                  <tr key={a.id}>
                    <td className="font-mono text-xs">{a.customer_id}</td>
                    <td className="font-medium">{a.descriptive_name}</td>
                    <td>
                      <StatusBadge tone={a.is_manager ? "primary" : "muted"}>
                        {a.is_manager ? "MCC" : "Client"}
                      </StatusBadge>
                    </td>
                    <td className="font-mono text-xs text-muted-foreground">{a.parent_customer_id || "—"}</td>
                    <td className="font-mono text-xs">{a.currency_code}</td>
                    <td>
                      <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!a.active}
                          disabled={a.is_manager}
                          onChange={(e) => patch(a.id, { active: e.target.checked })}
                          data-testid={`ga-active-toggle-${a.customer_id}`}
                          className="w-3.5 h-3.5 accent-[hsl(var(--primary))]"
                        />
                        <span className="text-xs">{a.is_manager ? "—" : a.active ? "On" : "Off"}</span>
                      </label>
                    </td>
                    <td>
                      <select
                        value={a.mapped_client_id || ""}
                        onChange={(e) => patch(a.id, { mapped_client_id: e.target.value })}
                        disabled={a.is_manager}
                        data-testid={`ga-map-client-${a.customer_id}`}
                        className="h-8 px-2 border border-border rounded-sm text-xs bg-card disabled:opacity-50"
                      >
                        <option value="">— Not mapped —</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="text-[11px]">
                      {last ? (
                        last.ok ? (
                          <span className="text-[hsl(var(--success))]" title={`Inserted ${last.summary?.campaigns_inserted ?? 0}, updated ${last.summary?.campaigns_updated ?? 0}, fetched ${last.summary?.campaigns_fetched ?? 0}`}>
                            OK · {last.summary?.campaigns_fetched ?? 0} fetched
                            {(last.summary?.campaigns_inserted || 0) + (last.summary?.campaigns_updated || 0) > 0
                              ? ` · ${last.summary?.campaigns_inserted || 0}+ / ${last.summary?.campaigns_updated || 0}~ saved`
                              : ""}
                          </span>
                        ) : (
                          <span
                            className="text-[hsl(var(--critical))]"
                            data-testid={`ga-fail-${a.customer_id}`}
                            title={last.error || "Unknown error"}
                          >
                            {last.failure_severity === "expected" ? "⊘ " : "✕ "}
                            {last.failure_label || "Failed"}
                          </span>
                        )
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td>
                      <button
                        onClick={() => testOne(a)}
                        disabled={a.is_manager}
                        data-testid={`ga-test-${a.customer_id}`}
                        className="h-7 px-2 text-[11px] font-medium border border-border hover:bg-secondary rounded-sm disabled:opacity-50"
                      >
                        Test
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {batchResults && (
        <div className="bg-card border border-border rounded-sm p-5" data-testid="ga-batch-results">
          <div className="label-mono mb-1">Test Results</div>
          <div className="font-heading text-lg tracking-tight mb-3">
            {batchResults.total ? `${batchResults.succeeded}/${batchResults.total} succeeded` : "Single-account result"}
          </div>
          {batchResults.totals && (
            <div className="text-[11px] grid grid-cols-2 md:grid-cols-4 gap-1 mb-4 p-3 bg-secondary/40 border border-border rounded-sm">
              <div><span className="text-muted-foreground">Camps inserted</span> <b className="tabular ml-1">{batchResults.totals.campaigns_inserted}</b></div>
              <div><span className="text-muted-foreground">Camps updated</span> <b className="tabular ml-1">{batchResults.totals.campaigns_updated}</b></div>
              <div><span className="text-muted-foreground">Metrics inserted</span> <b className="tabular ml-1">{batchResults.totals.metrics_inserted}</b></div>
              <div><span className="text-muted-foreground">Metrics updated</span> <b className="tabular ml-1">{batchResults.totals.metrics_updated}</b></div>
            </div>
          )}
          <div className="space-y-2">
            {batchResults.results.map((r, i) => (
              <div key={i} className={`border rounded-sm p-3 ${r.ok ? "border-[hsl(var(--success))]/30" : (r.failure_severity === "expected" ? "border-border" : "border-[hsl(var(--critical))]/30")}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-sm">{r.descriptive_name} <span className="font-mono text-xs text-muted-foreground">· {r.customer_id}</span></div>
                  <StatusBadge tone={r.ok ? "success" : (r.failure_severity === "expected" ? "muted" : "critical")} solid={!r.ok && r.failure_severity !== "expected"}>
                    {r.ok ? "OK" : (r.failure_severity === "expected" ? "SKIP" : "FAIL")}
                  </StatusBadge>
                </div>
                {r.ok ? (
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 tabular">
                    <span>Fetched: <b className="text-foreground">{r.summary?.campaigns_fetched ?? 0}</b></span>
                    <span>Inserted: <b className="text-[hsl(var(--success))]">{r.summary?.campaigns_inserted ?? 0}</b></span>
                    <span>Updated: <b className="text-foreground">{r.summary?.campaigns_updated ?? 0}</b></span>
                    <span>Metrics: <b className="text-foreground">+{r.summary?.metrics_inserted ?? 0} / ~{r.summary?.metrics_updated ?? 0}</b></span>
                    <span>Landing: <b className="text-foreground">+{r.summary?.landing_pages_inserted ?? 0} / ~{r.summary?.landing_pages_updated ?? 0}</b></span>
                    {r.summary?.impressions_total != null && (
                      <span>Impr: <b className="text-foreground">{r.summary.impressions_total.toLocaleString()}</b></span>
                    )}
                    {r.summary?.clicks_total != null && (
                      <span>Clicks: <b className="text-foreground">{r.summary.clicks_total.toLocaleString()}</b></span>
                    )}
                    {r.summary?.cost_total != null && (
                      <span>Cost: <b className="text-foreground">{r.summary.cost_total.toLocaleString()}</b></span>
                    )}
                  </div>
                ) : (
                  <div className="text-xs">
                    <div className={r.failure_severity === "expected" ? "text-muted-foreground" : "text-[hsl(var(--critical))]"}>
                      {r.failure_severity === "expected" ? "⊘ " : "✕ "}
                      <b>{r.failure_label || "Failed"}</b>
                    </div>
                    {r.error && (
                      <details className="mt-1 text-[10px] text-muted-foreground">
                        <summary className="cursor-pointer hover:text-foreground">Show full error</summary>
                        <pre className="mt-1 p-2 bg-muted rounded-sm whitespace-pre-wrap break-all max-h-40 overflow-auto">{r.error}</pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GoogleAdsConnectorPanel() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [clients, setClients] = useState([]);
  const [logs, setLogs] = useState([]);
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [form, setForm] = useState({
    client_id: "", client_secret: "", developer_token: "",
    redirect_uri: REDIRECT_URI, mode: "mock",
  });
  const [saving, setSaving] = useState(false);
  const popupRef = useRef(null);

  const reloadLogs = async () => {
    try { const { data } = await api.get("/auth/google-ads/test-logs", { params: { limit: 20 } }); setLogs(data); } catch {}
  };

  const reloadAll = async () => {
    try {
      const [c, s, p, cl] = await Promise.all([
        api.get("/admin/connectors/google-ads/config"),
        api.get("/connectors/google-ads/status"),
        api.get("/google-ads/profiles"),
        api.get("/clients"),
      ]);
      setCfg(c.data); setStatus(s.data); setProfiles(p.data); setClients(cl.data);
      setForm((f) => ({ ...f, mode: c.data.mode || "mock", redirect_uri: c.data.redirect_uri || REDIRECT_URI }));
      reloadLogs();
    } catch (e) { /* noop */ }
  };

  useEffect(() => { reloadAll(); }, []);

  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.data?.type === "ga_oauth") {
        if (ev.data.ok) {
          toast.success(`Google connected · ${ev.data.payload?.email || ""}`);
          setTimeout(() => { reloadAll(); if (ev.data.payload?.profile_id) setExpandedProfile(ev.data.payload.profile_id); }, 400);
        } else {
          toast.error(`OAuth failed: ${ev.data.payload?.error || "unknown"}`);
        }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const save = async () => {
    if (!form.client_id || !form.client_secret || !form.developer_token || !form.redirect_uri) {
      return toast.error("All 4 fields are required");
    }
    setSaving(true);
    try {
      await api.put("/admin/connectors/google-ads/config", form);
      toast.success("Credentials saved");
      setForm((f) => ({ ...f, client_id: "", client_secret: "", developer_token: "" }));
      reloadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const switchMode = async (mode) => {
    try {
      await api.put("/admin/connectors/google-ads/mode", { mode });
      toast.success(`Mode: ${mode.toUpperCase()}`);
      reloadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startOauth = async () => {
    try {
      const { data } = await api.get("/auth/google-ads/connect");
      popupRef.current = window.open(data.auth_url, "ga_oauth", "width=560,height=720,menubar=no,toolbar=no");
      if (!popupRef.current) toast.error("Popup blocked — allow popups for this site");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start OAuth"); }
  };

  const removeProfile = async (p) => {
    if (!window.confirm(`Remove "${p.connected_google_email}"?\nThis deletes ${p.account_count} discovered accounts and the stored refresh token.`)) return;
    try {
      await api.delete(`/google-ads/profiles/${p.id}`);
      toast.success("Profile removed");
      if (expandedProfile === p.id) setExpandedProfile(null);
      reloadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  const mode = status?.mode || "mock";
  const canConnect = cfg?.configured && mode === "live";
  const headerStatusTone = !cfg?.configured ? "muted"
    : mode !== "live" ? "muted"
    : (status?.profile_count || 0) > 0 ? "success" : "warning";
  const headerStatusLabel = !cfg?.configured ? "Not Configured"
    : mode !== "live" ? "Mock Mode"
    : (status?.profile_count || 0) > 0 ? `Live · ${status.profile_count} profile(s)` : "Live · No profile yet";

  return (
    <div className="space-y-6" data-testid="ga-connector-panel">
      {/* Header */}
      <div className="bg-card border border-border rounded-sm p-5 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/20 flex items-center justify-center rounded-sm">
            <Database className="w-5 h-5 text-primary" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-heading font-medium text-base">Google Ads Connector</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Multi-profile OAuth · MCC hierarchy · Account mapping · Batch live tests
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge tone={mode === "live" ? "primary" : "muted"}>Mode: {mode.toUpperCase()}</StatusBadge>
          <StatusBadge tone={headerStatusTone}>{headerStatusLabel}</StatusBadge>
        </div>
      </div>

      {/* Status KPIs */}
      {status && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-card border border-border rounded-sm p-4 kpi-border-primary">
            <div className="label-mono">Google Profiles</div>
            <div className="font-heading text-2xl font-light tabular mt-1">{status.profile_count}</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4 kpi-border-success">
            <div className="label-mono">Total Accounts</div>
            <div className="font-heading text-2xl font-light tabular mt-1">{status.total_accounts}</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4 kpi-border-warning">
            <div className="label-mono">Active for sync</div>
            <div className="font-heading text-2xl font-light tabular mt-1">{status.active_accounts}</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4 kpi-border-ai">
            <div className="label-mono">Mapped to Clients</div>
            <div className="font-heading text-2xl font-light tabular mt-1">{status.mapped_accounts}</div>
          </div>
        </div>
      )}

      {/* Setup guide */}
      <div className="bg-card border border-border border-l-2 border-l-[hsl(var(--ai))] rounded-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[hsl(var(--ai))]" />
          <div className="label-mono text-[hsl(var(--ai))]">Setup guide</div>
        </div>
        <ol className="text-sm space-y-2 list-decimal pl-5 text-muted-foreground">
          <li>Create <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">OAuth 2.0 Client ID <ExternalLink className="w-3 h-3" /></a> · Web application.</li>
          <li>Add the exact Redirect URI below to Authorized redirect URIs.</li>
          <li>Enable <a href="https://console.cloud.google.com/apis/library/googleads.googleapis.com" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">Google Ads API <ExternalLink className="w-3 h-3" /></a>.</li>
          <li>Get a <a href="https://ads.google.com/aw/apicenter" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">Developer Token <ExternalLink className="w-3 h-3" /></a>.</li>
          <li>Save credentials, switch to <span className="font-mono">LIVE</span>, then connect one or more Google accounts.</li>
        </ol>
      </div>

      {/* Redirect URI */}
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="label-mono mb-1">Redirect URI · Add this in Google Cloud Console</div>
        <div className="flex items-center gap-2 mt-2">
          <input readOnly value={REDIRECT_URI} data-testid="ga-redirect-uri-input"
            className="flex-1 h-10 px-3 border border-border rounded-sm text-sm font-mono bg-secondary" />
          <CopyBtn value={REDIRECT_URI} testId="copy-redirect-uri" />
        </div>
      </div>

      {/* Credentials */}
      <div className="bg-card border border-border rounded-sm p-5" data-testid="ga-config-form">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-mono">Credentials</div>
            <div className="font-heading text-lg tracking-tight mt-1">OAuth configuration</div>
          </div>
          <div className="flex items-center gap-1 border border-border rounded-sm p-1">
            {["mock", "live"].map((m) => (
              <button key={m} data-testid={`ga-mode-${m}`} onClick={() => switchMode(m)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-sm ${
                  mode === m ? (m === "live" ? "bg-primary text-primary-foreground" : "bg-secondary") : "text-muted-foreground hover:bg-secondary"
                }`}>{m}</button>
            ))}
          </div>
        </div>
        {cfg?.configured && (
          <div className="mb-4 p-3 bg-secondary/50 border border-border rounded-sm text-xs flex items-center justify-between">
            <div>
              <span className="label-mono">Currently saved</span>
              <span className="ml-3 font-mono">Client ID: {cfg.client_id_masked}</span>
              <span className="ml-3 font-mono">Secret: {cfg.client_secret_masked}</span>
              <span className="ml-3 font-mono">Dev Token: {cfg.developer_token_masked}</span>
            </div>
            <span className="text-muted-foreground">Updated by {cfg.updated_by}</span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="OAuth Client ID"><input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="xxx.apps.googleusercontent.com" data-testid="ga-client-id" className="w-full h-10 px-3 border border-border rounded-sm text-sm font-mono" /></Field>
          <Field label="OAuth Client Secret"><input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} placeholder="GOCSPX-…" data-testid="ga-client-secret" className="w-full h-10 px-3 border border-border rounded-sm text-sm font-mono" /></Field>
          <Field label="Developer Token"><input type="password" value={form.developer_token} onChange={(e) => setForm({ ...form, developer_token: e.target.value })} placeholder="22-char token" data-testid="ga-developer-token" className="w-full h-10 px-3 border border-border rounded-sm text-sm font-mono" /></Field>
          <Field label="Redirect URI"><input value={form.redirect_uri} onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })} data-testid="ga-redirect-uri" className="w-full h-10 px-3 border border-border rounded-sm text-sm font-mono" /></Field>
        </div>
        <div className="mt-5 flex items-center gap-2">
          <button onClick={save} disabled={saving} data-testid="ga-save-credentials"
            className="h-10 px-4 bg-primary text-primary-foreground text-sm font-medium rounded-sm hover:bg-primary/90 disabled:opacity-60">
            {saving ? "Saving…" : "Save Credentials"}
          </button>
          <span className="text-xs text-muted-foreground">Credentials are stored server-side only and never returned to the browser.</span>
        </div>
      </div>

      {/* Connected profiles */}
      <div className="bg-card border border-border rounded-sm" data-testid="ga-profiles-panel">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="label-mono">Connected Google Accounts</div>
            <div className="font-heading text-lg tracking-tight mt-1">Profiles</div>
          </div>
          <button
            onClick={startOauth}
            disabled={!canConnect}
            data-testid="ga-connect-btn"
            className="h-10 px-4 flex items-center gap-2 text-sm font-medium bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50"
          >
            <UserPlus className="w-4 h-4" />
            {profiles.length > 0 ? "Connect Another Google Account" : "Connect Google Account"}
          </button>
        </div>

        {!canConnect && (
          <div className="m-5 flex items-start gap-2 p-3 border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 text-xs rounded-sm">
            <AlertTriangle className="w-4 h-4 text-[hsl(38_92%_30%)] shrink-0 mt-0.5" />
            <span>Save OAuth credentials above and switch to <strong>LIVE</strong> mode before connecting.</span>
          </div>
        )}

        <div className="divide-y divide-border">
          {profiles.length === 0 && canConnect && (
            <div className="p-8 text-center text-sm text-muted-foreground">No Google accounts connected yet.</div>
          )}
          {profiles.map((p) => {
            const expanded = expandedProfile === p.id;
            return (
              <div key={p.id} data-testid={`ga-profile-${p.id}`}>
                <div
                  className="px-5 py-3.5 flex items-center justify-between cursor-pointer hover:bg-secondary/50"
                  onClick={() => setExpandedProfile(expanded ? null : p.id)}
                >
                  <div className="flex items-center gap-3">
                    {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center text-[11px] font-semibold text-primary">
                      {(p.connected_google_email || "").slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{p.connected_google_email || "(no email)"}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {p.account_count} account(s) · Last discover {p.last_accounts_sync ? new Date(p.last_accounts_sync).toLocaleString() : "never"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge tone="success">Connected</StatusBadge>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeProfile(p); }}
                      data-testid={`ga-remove-profile-${p.id}`}
                      className="h-8 w-8 flex items-center justify-center border border-border hover:bg-[hsl(var(--critical))]/10 hover:border-[hsl(var(--critical))]/30 rounded-sm text-muted-foreground hover:text-[hsl(var(--critical))]"
                      title="Remove profile"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {expanded && (
                  <div className="px-5 pb-5 pt-2 bg-secondary/20">
                    <AccountsTable profileId={p.id} clients={clients} logs={logs} refreshLogs={reloadLogs} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity log */}
      <div className="bg-card border border-border rounded-sm p-5" data-testid="ga-test-logs">
        <div className="label-mono mb-1">Activity log</div>
        <div className="font-heading text-lg tracking-tight mb-3">Last 20 attempts</div>
        <div className="space-y-1">
          {logs.slice(0, 20).map((l, i) => (
            <div key={i} className="flex items-center gap-3 py-1.5 border-b border-border last:border-b-0 text-xs">
              <StatusBadge tone={l.ok ? "success" : "critical"}>{l.ok ? "OK" : "FAIL"}</StatusBadge>
              <span className="font-medium w-40 truncate">{l.kind}</span>
              {l.customer_id && <span className="font-mono text-[11px] text-muted-foreground">{l.customer_id}</span>}
              <span className="font-mono text-muted-foreground flex-1 truncate">{JSON.stringify(l.payload)}</span>
              <span className="text-muted-foreground">{new Date(l.at).toLocaleString()}</span>
            </div>
          ))}
          {logs.length === 0 && <div className="text-xs text-muted-foreground">No activity yet.</div>}
        </div>
      </div>
    </div>
  );
}
